<script setup lang="ts">
import { useLocale } from '@/plugins/translation';
import useHistory from '@/composables/use-history';
import { useVideos } from '@ilteducation/videos';
import { computed } from 'vue';
import { SubjectVideo } from '@ilteducation/videos';
import WatchedVideo from './WatchedVideo.vue';
import { useUserContext } from '@ilteducation/ilt-auth';

type Props = {
  userUid: string;
  isStudent?: boolean;
};

const props = withDefaults(defineProps<Props>(), { isStudent: false });

const { t, l } = useLocale();
const { watchHistory, isLoading: isLoadingHistory } = useHistory(props.userUid);
const videoStore = useVideos();
const { realm } = useUserContext();
const watchedVideos = computed(() =>
  videoStore.getVideoByResourceIds(watchHistory.value.map((watchedVideo) => watchedVideo.resourceId)),
);
const watchedVideosWithSubject = computed(() =>
  // find which subject was watched if multi-subject video
  watchHistory.value
    .map((trace) => {
      const watchedVideo = watchedVideos.value.find((wv) => wv.id === trace.resourceId);
      if (!watchedVideo) {
        return undefined;
      }
      const subjectId = trace.categoryId ?? watchedVideo.videos?.find((v) => v.market === realm)?.categoryId;

      if (!subjectId) {
        return undefined;
      }
      return { ...watchedVideo, subjectId };
    })
    .filter((video): video is SubjectVideo => video !== undefined),
);
</script>

<template>
  <v-container class="custom-container pa-4">
    <v-row v-if="watchHistory && watchHistory.length > 0">
      <v-col v-for="(watchedVideo, index) in watchedVideosWithSubject" :key="index" xs="3" sm="6" md="6" lg="3">
        <WatchedVideo :video="watchedVideo" />
      </v-col>
    </v-row>
    <v-row v-else-if="isLoadingHistory">
      <v-col>
        <v-progress-linear indeterminate color="primary" :aria-label="t(l.label_loading)" />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col>
        <p v-if="isStudent">{{ t(l.user_page.no_videos) }}</p>
        <p v-else>{{ t(l.my_page.no_videos) }}</p>
      </v-col>
    </v-row>
  </v-container>
</template>
